import {
  ItemPlanilha,
  StatusOrcamento,
  TipoItem,
  TIPOS_ITEM_SEM_COTACAO,
} from "@models/index";

export type ItemPlanilhaLegacy = ItemPlanilha & {
  statusOrcamento?: StatusOrcamento;
};

export const normalizeItemStatus = (item: ItemPlanilhaLegacy): ItemPlanilha => {
  const { statusOrcamento, ...rest } = item;
  if (rest.status === undefined && statusOrcamento) {
    return { ...rest, status: statusOrcamento } as ItemPlanilha;
  }
  return rest as ItemPlanilha;
};

// Espelha StatusEnvio.JaSolicitados da API: status que indicam que o pedido já
// saiu para o fornecedor. Vínculos sem status, "aguardando" ou "aguardando_envio"
// contam como pendentes — o banco usa os três para "ainda não solicitado".
const STATUS_JA_SOLICITADO = new Set<string>([
  StatusOrcamento.ENVIADA,
  StatusOrcamento.REENVIADA,
  StatusOrcamento.RESPONDIDA,
  StatusOrcamento.REJEITADA,
]);

export const solicitacaoJaEnviada = (status?: string | null): boolean =>
  !!status && STATUS_JA_SOLICITADO.has(status);

export const applyStatusToFornecedor = (
  row: ItemPlanilha,
  status: StatusOrcamento,
): ItemPlanilha => {
  const { statusOrcamento, ...rest } = row as ItemPlanilhaLegacy;
  return { ...rest, status };
};

export const corrigirNumerosFornecedores = (
  itens: ItemPlanilha[],
): ItemPlanilha[] => {
  const itensCorrigidos: ItemPlanilha[] = [];
  let itemPaiAtual: ItemPlanilha | null = null;
  let contadorFornecedores = 0;

  for (let i = 0; i < itens.length; i++) {
    const item = itens[i] as ItemPlanilhaLegacy;
    const itemNormalizado = normalizeItemStatus(item);

    if (
      itemNormalizado.tipo === TipoItem.ITEM ||
      TIPOS_ITEM_SEM_COTACAO.includes(itemNormalizado.tipo)
    ) {
      itemPaiAtual = itemNormalizado;
      contadorFornecedores = 0;
      itensCorrigidos.push(itemNormalizado);
    } else if (itemNormalizado.tipo === TipoItem.FORNECEDOR) {
      contadorFornecedores++;
      const numeroCorrigido =
        itemPaiAtual &&
        (!itemNormalizado.numero || itemNormalizado.numero === "")
          ? `${itemPaiAtual.numero}.${String(contadorFornecedores).padStart(2, "0")}`
          : itemNormalizado.numero;

      itensCorrigidos.push({
        ...itemNormalizado,
        numero: numeroCorrigido,
      });
    } else {
      itensCorrigidos.push(itemNormalizado);
    }
  }

  return itensCorrigidos;
};

export const recalcularTotaisEMenorPreco = (
  itens: ItemPlanilha[],
): ItemPlanilha[] => {
  const itensRecalculados: ItemPlanilha[] = [];

  for (let i = 0; i < itens.length; i++) {
    const item = { ...itens[i] };

    if (
      (item.tipo === TipoItem.ITEM ||
        TIPOS_ITEM_SEM_COTACAO.includes(item.tipo)) &&
      item.numero
    ) {
      // Linha amarela de valor manual (TAXAS, TRANSPORTES): não recalcula nada
      // a partir de fornecedor, só repassa as linhas brancas que a seguem.
      if (TIPOS_ITEM_SEM_COTACAO.includes(item.tipo)) {
        const tipoLinhaBranca =
          item.tipo === TipoItem.ITEM_TRANSPORTE
            ? TipoItem.DESCRITIVO_TRANSPORTE
            : TipoItem.DESCRITIVO_TAXAS;

        const linhasDescritivo: ItemPlanilha[] = [];

        for (let j = i + 1; j < itens.length; j++) {
          if (itens[j].tipo !== tipoLinhaBranca) break;
          linhasDescritivo.push(itens[j]);
        }

        // Manter os valores manuais do ITEM_TAXAS (não recalcular)
        itensRecalculados.push(item);

        // Adicionar linhas DESCRITIVO_TAXAS sem modificação
        linhasDescritivo.forEach((linha) => {
          itensRecalculados.push(
            normalizeItemStatus(linha as ItemPlanilhaLegacy),
          );
        });

        i += linhasDescritivo.length;
      } else {
        // Lógica existente para ITEM normal (com fornecedores)
        const fornecedores: ItemPlanilha[] = [];
        let menorPreco = Infinity;
        let indiceMenorPreco = -1;

        for (let j = i + 1; j < itens.length; j++) {
          if (itens[j].tipo !== TipoItem.FORNECEDOR) break;

          fornecedores.push(itens[j]);
          const unitario = itens[j].unitario;

          // Só concorre quem finalizou a proposta. Preço salvo na etapa de
          // valores, sem o fornecedor chegar ao fim, não é cotação entregue:
          // não tem assinatura nem PDF, e vencia o menor preço fazendo a
          // planilha precificar o evento com uma proposta que ninguém deu.
          //
          // Reenvio continua valendo, porque mantém a data da resposta
          // anterior — o critério separa "abandonou" de "já respondeu antes".
          const finalizou = !!itens[j].dataResposta;

          if (
            finalizou &&
            unitario != null &&
            unitario > 0 &&
            unitario < menorPreco
          ) {
            menorPreco = unitario;
            indiceMenorPreco = j;
          }
        }

        if (menorPreco !== Infinity) {
          item.unitario = menorPreco;

          if (item.quantidade && item.diaria) {
            item.subtotal = item.quantidade * item.diaria * menorPreco;
            item.total = item.subtotal;
          }
        }

        itensRecalculados.push(item);

        fornecedores.forEach((forn, idx) => {
          itensRecalculados.push({
            ...normalizeItemStatus(forn as ItemPlanilhaLegacy),
            menorPreco: i + 1 + idx === indiceMenorPreco,
          });
        });

        i += fornecedores.length;
      }
    } else {
      itensRecalculados.push(item);
    }
  }

  return itensRecalculados;
};

export const updateItemWithLowestPrice = (
  data: ItemPlanilha[],
  itemRowIndex: number,
): ItemPlanilha[] => {
  const newRowData = [...data];
  const itemRow = { ...newRowData[itemRowIndex] };

  if (
    itemRow.tipo !== TipoItem.ITEM &&
    !TIPOS_ITEM_SEM_COTACAO.includes(itemRow.tipo)
  ) {
    return newRowData;
  }

  const fornecedorIndices: number[] = [];
  let lowestPrice = Infinity;

  for (let i = itemRowIndex + 1; i < newRowData.length; i++) {
    if (newRowData[i].tipo !== TipoItem.FORNECEDOR) break;

    const fornecedor = normalizeItemStatus(newRowData[i] as ItemPlanilhaLegacy);
    fornecedor.menorPreco = false;
    newRowData[i] = fornecedor;
    fornecedorIndices.push(i);

    const unitario = fornecedor.unitario;
    if (unitario != null && unitario > 0 && unitario < lowestPrice) {
      lowestPrice = unitario;
    }
  }

  fornecedorIndices.forEach((idx) => {
    const fornecedor = { ...newRowData[idx] };
    fornecedor.menorPreco =
      lowestPrice !== Infinity &&
      fornecedor.unitario != null &&
      fornecedor.unitario > 0 &&
      fornecedor.unitario === lowestPrice;
    newRowData[idx] = fornecedor;
  });

  if (lowestPrice !== Infinity) {
    itemRow.unitario = lowestPrice;

    const quantidade = itemRow.quantidade || 0;
    const diaria = itemRow.diaria || 0;
    const subtotal = quantidade * diaria * lowestPrice;

    itemRow.subtotal = subtotal;
    itemRow.total = subtotal;
  }

  newRowData[itemRowIndex] = itemRow;
  return newRowData;
};

/**
 * Ordem em que as cotações de um item devem aparecer: menor preço primeiro.
 *
 * Valor zero ou ausente vai para o fim, porque significa "sem proposta" e não
 * "de graça" — é a mesma regra que a planilha aplica às linhas de fornecedor.
 *
 * Existe aqui porque três lugares precisam concordar: as linhas na tela, os
 * orçamentos dentro do FACITE e os PDFs dentro do pacote ZIP. Quando a regra
 * ficava duplicada, um deles saía fora de ordem sem ninguém perceber.
 */
export function compararCotacoesPorPreco(
  a: { unitario?: number | null },
  b: { unitario?: number | null },
): number {
  const ua = a.unitario ?? Infinity;
  const ub = b.unitario ?? Infinity;
  if (ua === 0 && ub === 0) return 0;
  if (ua === 0) return 1;
  if (ub === 0) return -1;
  return ua - ub;
}
